// frontend/src/pages/ResetPasswordPage.jsx
import React, { useMemo, useState } from "react";
import { useParams, useSearchParams, Link, useNavigate } from "react-router-dom";
import api from "../services/api";

const getPasswordChecks = (password) => {
  const lengthOK = password.length >= 8;
  const upperOK = /[A-Z]/.test(password);
  const lowerOK = /[a-z]/.test(password);
  const numberOK = /[0-9]/.test(password);
  const symbolOK = /[^A-Za-z0-9]/.test(password);

  return { lengthOK, upperOK, lowerOK, numberOK, symbolOK };
};

const hasAllChecks = (checks) =>
  checks.lengthOK && checks.upperOK && checks.lowerOK && checks.numberOK && checks.symbolOK;

const PasswordCriteria = ({ password }) => {
  const checks = getPasswordChecks(password);

  const itemClass = (ok) =>
    "flex items-center text-xs mt-1 " + (ok ? "text-emerald-600" : "text-gray-500");

  const dotClass = (ok) =>
    "w-2 h-2 rounded-full mr-2 " + (ok ? "bg-emerald-500" : "bg-gray-300");

  return (
    <ul className="mt-2 space-y-0.5">
      <li className={itemClass(checks.lengthOK)}>
        <span className={dotClass(checks.lengthOK)}></span>
        Mínimo de 8 caracteres
      </li>
      <li className={itemClass(checks.upperOK)}>
        <span className={dotClass(checks.upperOK)}></span>
        Pelo menos 1 letra maiúscula
      </li>
      <li className={itemClass(checks.lowerOK)}>
        <span className={dotClass(checks.lowerOK)}></span>
        Pelo menos 1 letra minúscula
      </li>
      <li className={itemClass(checks.numberOK)}>
        <span className={dotClass(checks.numberOK)}></span>
        Pelo menos 1 número
      </li>
      <li className={itemClass(checks.symbolOK)}>
        <span className={dotClass(checks.symbolOK)}></span>
        Pelo menos 1 símbolo (ex: !, @, #, $)
      </li>
    </ul>
  );
};

export default function ResetPasswordPage() {
  const params = useParams();
  const [qs] = useSearchParams();
  const navigate = useNavigate();

  const token = useMemo(
    () => params?.token || qs.get("token") || "",
    [params, qs]
  );

  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");

    if (!token) {
      setErr("Link inválido.");
      return;
    }

    const checks = getPasswordChecks(pwd);
    if (!hasAllChecks(checks)) {
      setErr("A senha não atende aos critérios mínimos de segurança.");
      return;
    }

    if (pwd !== pwd2) {
      setErr("As senhas não conferem.");
      return;
    }

    try {
      setLoading(true);
      await api.post("/auth/reset-password", { token, new_password: pwd });
      setDone(true);
      setTimeout(() => navigate("/login"), 2000);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Falha ao redefinir senha.";
      setErr(msg);
    } finally {
      setLoading(false);
    }
  };

  const checks = getPasswordChecks(pwd);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-md bg-white border rounded-xl shadow-sm p-6">
        <h1 className="text-xl font-semibold">Redefinir senha</h1>
        <p className="text-sm text-gray-600 mt-1">
          Informe sua nova senha para acessar o OdontoAssist AI.
        </p>

        {!token && (
          <div className="mt-4 p-3 rounded-md bg-red-50 text-red-700 text-sm">
            Link inválido ou incompleto. Verifique o e-mail recebido ou solicite
            uma nova redefinição de senha.
          </div>
        )}

        {err && (
          <div className="mt-4 p-3 rounded-md bg-red-50 text-red-700 text-sm">
            {err}
          </div>
        )}

        {done ? (
          <div className="mt-6 p-3 rounded-md bg-emerald-50 text-emerald-700 text-sm">
            Senha atualizada com sucesso. Redirecionando para o login…
          </div>
        ) : (
          <form onSubmit={onSubmit} className="mt-6 space-y-4">
            <div>
              <label className="text-sm font-medium">Nova senha</label>
              <input
                type="password"
                className="mt-1 w-full border rounded-md px-3 py-2 focus:ring-emerald-500 focus:border-emerald-500"
                value={pwd}
                onChange={(e) => setPwd(e.target.value)}
                placeholder="Digite a nova senha"
              />
              <PasswordCriteria password={pwd} />
            </div>

            <div>
              <label className="text-sm font-medium">Confirmar senha</label>
              <input
                type="password"
                className="mt-1 w-full border rounded-md px-3 py-2 focus:ring-emerald-500 focus:border-emerald-500"
                value={pwd2}
                onChange={(e) => setPwd2(e.target.value)}
                placeholder="Repita a nova senha"
              />
            </div>

            <button
              type="submit"
              disabled={loading || !token}
              className="w-full mt-2 rounded-md bg-emerald-600 text-white py-2 text-sm font-medium hover:bg-emerald-700 disabled:opacity-60"
            >
              {loading ? "Salvando..." : "Salvar nova senha"}
            </button>
          </form>
        )}

        <div className="mt-6 text-center text-sm">
          <Link to="/login" className="text-emerald-700 hover:underline">
            Voltar ao login
          </Link>
        </div>
      </div>
    </div>
  );
}
